"use client";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@relume_io/relume-ui";
import { AnimatePresence, motion } from "framer-motion";
import React, { useState, useEffect } from "react";
import { BlogPostModel } from "../../services/airtable/models/blogPost";
import { BlogPostCard } from './BlogPostCard';

const MotionTabsContent = motion(TabsContent);

const useRelume = ({ defaultValue }) => {
  const [activeTab, setActiveTab] = useState(defaultValue);

  const isActive = (tabValue) => {
    return activeTab === tabValue ? 1 : 0;
  };

  return {
    activeTab,
    setActiveTab,
    isActive,
  };
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

export function BlogListingPage() {
  const useActive = useRelume({ defaultValue: "view-all" });
  const [featuredPost, setFeaturedPost] = useState(null);
  const [nonFeaturedPosts, setNonFeaturedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        console.log('BlogListingPage: Starting to load posts...');
        const [featured, others] = await Promise.all([
          BlogPostModel.getFeaturedPost(),
          BlogPostModel.getNonFeaturedPosts()
        ]);
        setFeaturedPost(featured);
        setNonFeaturedPosts(others);
      } catch (error) {
        console.error('BlogListingPage: Error loading posts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <section id="relume" className="px-[5%] py-16 md:py-24 lg:py-28">
      <div className="container">
        <div className="rb-12 mb-12 w-full max-w-lg md:mb-18 lg:mb-20">
          <div className="w-full max-w-lg">
            <p className="mb-3 font-semibold md:mb-4">Blog</p>
            <h1 className="mb-5 text-6xl font-bold md:mb-6 md:text-9xl lg:text-10xl">
              Explore Our Latest Insights
            </h1>
            <p className="md:text-md">
              Discover the art of narrative creation.
            </p>
          </div>
        </div>
        {featuredPost && (
          <div className="rb-12 mb-12 grid grid-cols-1 items-center border border-border-primary md:mb-16 md:grid-cols-2">
            <a href={`/blog/${featuredPost.slug}`} className="size-full">
              <img
                src={featuredPost.titleImageUrl || "https://d22po4pjz3o32e.cloudfront.net/placeholder-image-landscape.svg"}
                alt={featuredPost.title}
                className="aspect-[8/6] size-full object-cover"
              />
            </a>
            <div className="flex h-full flex-col items-start justify-between px-5 py-6 md:p-8 lg:p-12">
              <div className="flex h-full w-full flex-col items-start justify-between">
                <div className="flex flex-col">
                  <a
                    href="#"
                    className="mb-2 mr-4 inline-block max-w-full text-sm font-semibold"
                  >
                    {featuredPost.category}
                  </a>
                  <a className="mb-2" href={`/blog/${featuredPost.slug}`}>
                    <h3 className="mb-2 text-2xl font-bold md:text-3xl md:leading-[1.3] lg:text-4xl">
                      {featuredPost.title}
                    </h3>
                  </a>
                  <p>{featuredPost.subtitle}</p>
                </div>
                <div className="mt-6 flex items-center">
                  <div className="mr-4 shrink-0">
                    <img
                      src="https://d22po4pjz3o32e.cloudfront.net/placeholder-image.svg"
                      alt={`${featuredPost.author} avatar`}
                      className="size-12 min-h-12 min-w-12 rounded-full object-cover"
                    />
                  </div>
                  <div>
                    <h6 className="text-sm font-semibold">{featuredPost.author}</h6>
                    <div className="flex items-center">
                      <p className="text-sm">{formatDate(featuredPost.publishDate)}</p>
                      <span className="mx-2">•</span>
                      <p className="text-sm">{featuredPost.readTimeMinutes} min read</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <Tabs defaultValue="view-all" className="mb-12">
          <TabsList className="mb-8 flex w-full flex-wrap gap-4 bg-transparent">
            <TabsTrigger value="view-all" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              View all
            </TabsTrigger>
            <TabsTrigger value="ai-technology" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              AI Technology
            </TabsTrigger>
            <TabsTrigger value="industry-trends" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              Industry Trends
            </TabsTrigger>
            <TabsTrigger value="creative-writing" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              Creative Writing
            </TabsTrigger>
            <TabsTrigger value="storytelling-tips" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              Storytelling Tips
            </TabsTrigger>
            <TabsTrigger value="case-studies" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              Case Studies
            </TabsTrigger>
            <TabsTrigger value="best-practices" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              Best Practices
            </TabsTrigger>
            <TabsTrigger value="product-updates" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              Product Updates
            </TabsTrigger>
          </TabsList>

          <AnimatePresence initial={false}>
            <MotionTabsContent value="view-all" className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {nonFeaturedPosts.map((post) => (
                <BlogPostCard 
                  key={post.id}
                  title={post.title}
                  excerpt={post.subtitle}
                  category={post.category}
                  date={post.publishDate}
                  author={post.author}
                  readTime={post.readTimeMinutes}
                  slug={post.slug}
                  imageUrl={post.titleImageUrl}
                />
              ))}
            </MotionTabsContent>
          </AnimatePresence>
        </Tabs>
      </div>
    </section>
  );
}
